/**
 * ReadinessCheckPage — Day-5 "Before you start" self-reflection at
 * /pleasure-or-problem/reflect?token=... .
 *
 * Six questions, on one scrolling page.  Answers save on interaction so the
 * backend can measure real per-question drop-off (Q1..Q4 timestamps but no
 * Q5 timestamp = the user gave up at Q5).  Closed-choice for Q1/3/4/5,
 * free-text for Q2/6, per Tudor's spec.  No score, no feedback.  On final
 * "Start the Axiom Recovery Programme" click, we mark the check as
 * submitted, record the Day-5 CTA click for CTR analytics, and forward
 * the user to ARP.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';

const CLOSED = {
  1: [
    { value: 'none',        label: 'None' },
    { value: 'one_or_two',  label: 'One or two' },
    { value: 'several',     label: 'Several' },
  ],
  3: [
    { value: 'no',            label: 'No, none of them' },
    { value: 'one',           label: 'Yes, one of them' },
    { value: 'more_than_one', label: 'Yes, more than one' },
  ],
  4: [
    { value: 'physical',   label: 'Physical' },
    { value: 'mental',     label: 'Mental' },
    { value: 'emotional',  label: 'Emotional' },
    { value: 'spiritual',  label: 'Spiritual' },
  ],
  5: [
    { value: 'yes_and_ready',   label: 'Yes and I plan to attend / arrange it' },
    { value: 'yes_not_ready',   label: 'Yes, but I\'m not ready to act on it yet' },
    { value: 'not_yet',         label: 'Not yet' },
    { value: 'no',              label: 'No' },
  ],
};

const QUESTIONS = [
  { num: 1, kind: 'closed', title: 'How many of the Day-1 signs did you recognise in yourself?',
    prompt: 'Go back to the pattern you wrote down on Day 1.' },
  { num: 2, kind: 'text', title: 'Does the sentence you used on Day 2 still hold up?',
    prompt: 'Think about the sentence you wrote down to excuse your drinking or using. Answer in your own words.' },
  { num: 3, kind: 'closed', title: 'Has your life come close to any of the three destinations?',
    prompt: 'Of death, insanity or incarceration: the three destinations this disease leads to when untreated.' },
  { num: 4, kind: 'closed', title: 'Which of the four Recovery dimensions is furthest out of balance for you right now?',
    prompt: 'Physical, mental, emotional or spiritual, from Day 4.' },
  { num: 5, kind: 'closed', title: 'Have you found out what medical detox or twelve-step meetings are available where you live?',
    prompt: 'Day 4 asked you to check. Have you?' },
  { num: 6, kind: 'text', title: 'In one sentence, what do you want to be different a year from now?',
    prompt: 'Your own words. Doesn\'t need to be tidy: just honest.' },
];

export default function ReadinessCheckPage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = params.get('token') || '';
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingQ, setSavingQ] = useState(null); // question number currently saving
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // ---- Load existing answers on mount ----
  useEffect(() => {
    if (!token) { setError('This link is missing a token. Please open the reflection link from your Day-5 email.'); setLoading(false); return; }
    (async () => {
      try {
        const resp = await fetch(`${apiBase}/api/individual-course/reflection/${token}`);
        if (!resp.ok) throw new Error('Readiness-check link not recognised.');
        const data = await resp.json();
        setFirstName(data.first_name || '');
        setAnswers({
          1: data.answers.q1 || '', 2: data.answers.q2 || '', 3: data.answers.q3 || '',
          4: data.answers.q4 || '', 5: data.answers.q5 || '', 6: data.answers.q6 || '',
        });
        if (data.submitted_at) setSubmitted(true);
      } catch (e) { setError(e.message || 'Failed to load the readiness check.'); }
      finally { setLoading(false); }
    })();
  }, [apiBase, token]);

  // ---- Save one question ----
  const saveAnswer = useCallback(async (num, val) => {
    if (!val || !val.toString().trim()) return;
    setSavingQ(num);
    try {
      await fetch(`${apiBase}/api/individual-course/reflection/${token}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_num: num, answer: val }),
      });
    } catch {
      /* silent; the final submit will surface a visible error */
    } finally { setSavingQ(null); }
  }, [apiBase, token]);

  // ---- Final submit ----
  const submit = async () => {
    setSubmitting(true);
    try {
      // Force-save any open text answers first.
      await Promise.all([2, 6].map(n => (answers[n] ? saveAnswer(n, answers[n]) : null)));
      const resp = await fetch(`${apiBase}/api/individual-course/reflection/${token}/submit`, { method: 'POST' });
      if (!resp.ok) throw new Error('Could not submit: please try again.');
      const data = await resp.json();
      setSubmitted(true);
      // Redirect to ARP after a beat so the user sees the confirmation state.
      setTimeout(() => { window.location.href = data.redirect_url; }, 1500);
    } catch (e) { setError(e.message || 'Submit failed.'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  }

  if (error && !Object.keys(answers).length) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="max-w-lg text-center" data-testid="reflect-error">
          <img src="/logo512.png" alt="Axiom" className="w-14 h-14 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-3">Link not recognised</h1>
          <p className="text-zinc-600 text-sm">{error}</p>
          <p className="text-xs text-zinc-500 mt-6">Reply to your Day-5 email or write to <a href="mailto:recovery@tudor8.com" className="underline">recovery@tudor8.com</a> and we'll sort it out.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4 py-12">
        <div className="max-w-lg text-center" data-testid="reflect-submitted">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-3">Taking you to ARP…</h1>
          <p className="text-zinc-600 text-sm">Your answers are yours to keep. You can revisit or delete them any time from this same link.</p>
        </div>
      </div>
    );
  }

  const answered = Object.values(answers).filter(v => v && v.toString().trim()).length;

  return (
    <div className="min-h-screen bg-white pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <img src="/logo512.png" alt="Axiom" className="w-14 h-14 mb-8" />

        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Day 5: before you start</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 leading-[1.15] mb-4">A quick readiness check</h1>
        <p className="text-base text-zinc-700 leading-relaxed mb-2">
          Six short questions, answered honestly, with <strong>no score to pass or fail</strong>. This isn't a test.
          It's a way to arrive at ARP already knowing where you stand.
        </p>
        <p className="text-sm text-zinc-500 leading-relaxed mb-10 italic">
          Hi{firstName ? ` ${firstName}` : ''}. Your answers save as you go. They aren't scored or shared. They're yours to keep and yours to bring into your first week of ARP.
        </p>

        <div className="space-y-8">
          {QUESTIONS.map((q) => (
            <div key={q.num} className="pb-6 border-b border-zinc-100 last:border-b-0" data-testid={`reflect-q${q.num}`}>
              <div className="flex items-start gap-3 mb-1">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-bold flex items-center justify-center">{q.num}</span>
                <h2 className="text-base sm:text-lg font-bold text-zinc-900 leading-snug">{q.title}</h2>
              </div>
              <p className="text-sm text-zinc-500 ml-9 mb-4 leading-relaxed">{q.prompt}</p>
              <div className="ml-9">
                {q.kind === 'closed' ? (
                  <div className="space-y-2">
                    {CLOSED[q.num].map((opt) => (
                      <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${answers[q.num] === opt.value ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}>
                        <input
                          type="radio"
                          name={`q${q.num}`}
                          value={opt.value}
                          checked={answers[q.num] === opt.value}
                          onChange={() => {
                            setAnswers(a => ({ ...a, [q.num]: opt.value }));
                            saveAnswer(q.num, opt.value);
                          }}
                          className="h-4 w-4 text-zinc-900 border-zinc-300 focus:ring-zinc-900"
                          data-testid={`reflect-q${q.num}-${opt.value}`}
                        />
                        <span className="text-sm text-zinc-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <textarea
                    rows={3}
                    maxLength={800}
                    placeholder="Your own words: one sentence is enough."
                    value={answers[q.num] || ''}
                    onChange={(e) => setAnswers(a => ({ ...a, [q.num]: e.target.value }))}
                    onBlur={(e) => saveAnswer(q.num, e.target.value)}
                    className="w-full px-4 py-3 border border-zinc-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent leading-relaxed"
                    data-testid={`reflect-q${q.num}-text`}
                  />
                )}
                {savingQ === q.num && <p className="text-[11px] text-zinc-400 mt-2">Saving…</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t-2 border-zinc-900">
          <p className="text-xs text-zinc-500 mb-4">{answered}/6 answered</p>
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-zinc-900 text-white font-bold rounded-lg text-base hover:bg-zinc-800 active:scale-[0.99] transition disabled:opacity-60"
            data-testid="reflect-submit-btn"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending you to ARP…</>) : 'Start the Axiom Recovery Programme'}
          </button>
          <p className="text-center text-[11px] text-zinc-500 mt-3 leading-relaxed">
            Your answers aren't scored or shared. They're yours to keep and yours to bring into your first week of ARP.
          </p>
        </div>

        <div className="mt-12 p-4 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-600 leading-relaxed space-y-2">
          <p className="font-bold text-zinc-800 text-[11px] uppercase tracking-wider">Your data (Privacy Act 1988)</p>
          <p>
            Your answers are stored against your subscriber record for your own reference and to help us see which
            questions people find hardest to answer. Individual answers are never shared.
          </p>
          <p>
            You can view or delete your readiness-check answers at any time by returning to this same link: or write
            to <a href="mailto:recovery@tudor8.com" className="underline">recovery@tudor8.com</a>.
          </p>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-zinc-400">
          <ShieldCheck className="w-3.5 h-3.5" /> Served over HTTPS.
        </div>
      </div>
    </div>
  );
}
